/**
 * SchoolBrandingPanel — Admin-only co-branding config for the parent-view PDF.
 *
 * Lets an admin upload a school logo (PNG/JPG, ≤1MB), set a school name,
 * and pick primary + secondary hex colors. The values are stored on the
 * backend (`PUT /api/admin/school-branding`) and applied on every
 * Mini Business Plan PDF rendered for parents OR students.
 *
 * Defensive UX:
 *  - Live preview of hero color, accent color, header strip
 *  - Validates hex inputs client-side (server also enforces)
 *  - Logo is read into a base64 data URL on the client
 *  - Dirty / saving / saved states are visible
 *  - Disable toggle keeps the row but reverts PDFs to default WE Mentality theme
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCcw, Upload, Eye, Check, AlertTriangle, Loader2, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function isHex(v) {
  return typeof v === "string" && HEX_RE.test(v.trim());
}

export default function SchoolBrandingPanel() {
  const [config, setConfig] = useState({
    enabled: false,
    school_name: "",
    primary_color: "#7C3AED",
    secondary_color: "#7C3AED",
    logo_data_url: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const res = await axios.get(`${API}/admin/school-branding`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const d = res.data || {};
      setConfig({
        enabled: !!d.enabled,
        school_name: d.school_name || "",
        primary_color: d.primary_color || "#7C3AED",
        secondary_color: d.secondary_color || "#7C3AED",
        logo_data_url: d.logo_data_url || "",
      });
    } catch (err) {
      setError("Couldn't load school branding.");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogoFile = (file) => {
    if (!file) return;
    if (file.size > 1_500_000) {
      setError("Logo must be ≤ 1.5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setConfig((c) => ({ ...c, logo_data_url: reader.result }));
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const colorsValid =
    isHex(config.primary_color) && isHex(config.secondary_color);

  const save = async () => {
    setError("");
    if (!colorsValid) {
      setError("Both colors must be valid hex (e.g., #4F46E5).");
      return;
    }
    setSaving(true);
    try {
      const body = {
        enabled: config.enabled,
        school_name: (config.school_name || "").trim() || null,
        primary_color: config.primary_color,
        secondary_color: config.secondary_color,
        logo_data_url: config.logo_data_url || null,
      };
      await axios.put(`${API}/admin/school-branding`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setError("");
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/school-branding`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      await load();
      setSavedAt(new Date());
    } catch {
      setError("Couldn't disable branding.");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="border border-gray-200 rounded-lg p-5">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading school branding…
        </div>
      </div>
    );
  }

  return (
    <section
      className="border border-gray-200 rounded-lg p-5"
      data-testid="admin-school-branding-panel"
      aria-labelledby="school-branding-heading"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 id="school-branding-heading" className="font-semibold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-indigo-600" aria-hidden="true" />
            Co-Branded School PDF
          </h3>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Add your school's logo, name, and colors to every Mini Business Plan PDF.
            The WE Mentality stays as secondary branding. Toggle off to revert to the default theme.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
            className="w-4 h-4 accent-indigo-600"
            data-testid="school-branding-enabled-toggle"
            aria-label="Enable school co-branding"
          />
          {config.enabled ? "Enabled" : "Disabled"}
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Form column */}
        <div className="space-y-4">
          <div>
            <label htmlFor="sb-name" className="block text-xs font-semibold text-gray-700 mb-1">
              School name
            </label>
            <input
              id="sb-name"
              type="text"
              value={config.school_name}
              maxLength={80}
              placeholder="Sunrise Middle School"
              onChange={(e) => setConfig((c) => ({ ...c, school_name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 text-sm"
              data-testid="school-branding-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="sb-primary" className="block text-xs font-semibold text-gray-700 mb-1">
                Primary color (hero)
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="sb-primary-swatch"
                  type="color"
                  value={isHex(config.primary_color) ? config.primary_color : "#7C3AED"}
                  onChange={(e) => setConfig((c) => ({ ...c, primary_color: e.target.value }))}
                  className="w-10 h-10 border border-gray-300 rounded-md cursor-pointer"
                  aria-label="Primary color picker"
                />
                <input
                  id="sb-primary"
                  type="text"
                  value={config.primary_color}
                  onChange={(e) => setConfig((c) => ({ ...c, primary_color: e.target.value }))}
                  placeholder="#1E3A8A"
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-mono ${
                    isHex(config.primary_color) ? "border-gray-300" : "border-red-400 bg-red-50"
                  }`}
                  data-testid="school-branding-primary-input"
                  aria-invalid={!isHex(config.primary_color)}
                />
              </div>
            </div>
            <div>
              <label htmlFor="sb-secondary" className="block text-xs font-semibold text-gray-700 mb-1">
                Secondary color (accents)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={isHex(config.secondary_color) ? config.secondary_color : "#7C3AED"}
                  onChange={(e) => setConfig((c) => ({ ...c, secondary_color: e.target.value }))}
                  className="w-10 h-10 border border-gray-300 rounded-md cursor-pointer"
                  aria-label="Secondary color picker"
                />
                <input
                  id="sb-secondary"
                  type="text"
                  value={config.secondary_color}
                  onChange={(e) => setConfig((c) => ({ ...c, secondary_color: e.target.value }))}
                  placeholder="#F59E0B"
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-mono ${
                    isHex(config.secondary_color) ? "border-gray-300" : "border-red-400 bg-red-50"
                  }`}
                  data-testid="school-branding-secondary-input"
                  aria-invalid={!isHex(config.secondary_color)}
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="sb-logo" className="block text-xs font-semibold text-gray-700 mb-1">
              Logo (PNG / JPG, max 1.5 MB)
            </label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="sb-logo"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" aria-hidden="true" />
                Choose logo
              </label>
              <input
                id="sb-logo"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="sr-only"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
                data-testid="school-branding-logo-input"
              />
              {config.logo_data_url && (
                <button
                  type="button"
                  onClick={() => setConfig((c) => ({ ...c, logo_data_url: "" }))}
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove logo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preview column */}
        <div
          className="border border-gray-200 rounded-xl p-4 bg-gray-50"
          aria-label="Live preview of how the PDF header will look"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-2">
            <Eye className="w-4 h-4" aria-hidden="true" />
            Live preview
          </div>
          {/* Mock header strip */}
          <div
            className="flex items-center gap-3 pb-2 mb-3"
            style={{ borderBottom: `2px solid ${isHex(config.primary_color) ? config.primary_color : "#7C3AED"}` }}
          >
            {config.logo_data_url ? (
              <img
                src={config.logo_data_url}
                alt="School logo preview"
                className="w-10 h-10 object-contain rounded"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                LOGO
              </div>
            )}
            <div>
              <div
                className="text-sm font-bold"
                style={{ color: isHex(config.primary_color) ? config.primary_color : "#7C3AED" }}
              >
                {config.school_name || "Your School Name"}
              </div>
              <div className="text-xs italic text-gray-500">
                Presents a student-built Mini Business Plan
              </div>
            </div>
          </div>
          {/* Mock hero */}
          <div
            className="rounded-lg p-3 text-white"
            style={{ backgroundColor: isHex(config.primary_color) ? config.primary_color : "#7C3AED" }}
          >
            <div className="text-[10px] uppercase tracking-wider opacity-80">Mini Business Plan · v1</div>
            <div className="text-lg font-bold leading-tight">Lemon Sparks</div>
            <div className="text-xs opacity-90">CEO · Jordan</div>
          </div>
          {/* Mock section title */}
          <div
            className="mt-3 text-xs font-bold"
            style={{ color: isHex(config.secondary_color) ? config.secondary_color : "#7C3AED" }}
          >
            THE PROBLEM I'M SOLVING
          </div>
          {/* Mock footer line */}
          <div className="mt-3 pt-2 border-t border-gray-200 text-[11px] text-gray-500 flex justify-between">
            <span><b>{config.school_name || "Your School"}</b> — Powered by The WE Mentality</span>
            <span>Coached by 🍋 LemonBot</span>
          </div>
        </div>
      </div>

      {/* Errors / actions */}
      {error && (
        <div
          className="mt-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
          data-testid="school-branding-error"
        >
          <AlertTriangle className="w-4 h-4" aria-hidden="true" />
          {error}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !colorsValid}
          data-testid="school-branding-save-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save branding"}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={saving}
          data-testid="school-branding-disable-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-60"
        >
          <RefreshCcw className="w-4 h-4" aria-hidden="true" />
          Disable / reset
        </button>
        {savedAt && (
          <span
            className="text-xs text-emerald-700 inline-flex items-center gap-1"
            role="status"
            aria-live="polite"
          >
            <Check className="w-4 h-4" aria-hidden="true" />
            Saved {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
    </section>
  );
}
